import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL ? new URL('/api/v1/alerts', import.meta.env.VITE_API_URL).toString() : 'http://localhost:3000/api/v1/alerts';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Alert types
export enum AlertType {
  DATA_GAP = 'data_gap',
  GATEWAY_OFFLINE = 'gateway_offline',
}

export enum AlertSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

export enum AlertStatus {
  ACTIVE = 'active',
  ACKNOWLEDGED = 'acknowledged',
  RESOLVED = 'resolved',
}

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  description: string;
  gatewayId?: string;
  slaveId?: number;
  dataType?: string;
  lastDataTimestamp?: string;
  metadata?: Record<string, any>;
  firstDetectedAt: string;
  lastCheckedAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AlertStats {
  total: number;
  active: number;
  acknowledged: number;
  resolved: number;
  byType: {
    dataGap: number;
    gatewayOffline: number;
  };
  bySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
}

export interface AlertResponse {
  success: boolean;
  data: Alert[];
  count: number;
  total: number;
  pagination?: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  error?: string;
  message?: string;
}

export interface AlertStatsResponse {
  success: boolean;
  data: AlertStats;
  error?: string;
  message?: string;
}

export interface AlertAcknowledgeResponse {
  success: boolean;
  message: string;
  error?: string;
}

// Alert API
export const alertApi = {
  // Get all alerts with optional filtering and pagination
  getAll: async (params?: { 
    limit?: number; 
    offset?: number;
    status?: AlertStatus;
    gatewayId?: string;
    customerId?: string;
  }): Promise<AlertResponse> => {
    const response = await api.get('/', { params });
    return response.data;
  },

  // Get only active alerts
  getActive: async (): Promise<AlertResponse> => {
    const response = await api.get('/active');
    return response.data;
  },

  // Get alert statistics
  getStats: async (): Promise<AlertStatsResponse> => {
    const response = await api.get('/stats');
    return response.data;
  },

  // Acknowledge an alert
  acknowledge: async (alertId: string, acknowledgedBy: string): Promise<AlertAcknowledgeResponse> => {
    const response = await api.post(`/${alertId}/acknowledge`, { acknowledgedBy });
    return response.data;
  },

  // Manually trigger alert checks (admin only)
  triggerCheck: async (): Promise<AlertAcknowledgeResponse> => {
    const response = await api.post('/check');
    return response.data;
  },
};

// Helper functions
export const getAlertSeverityColor = (severity: AlertSeverity): string => {
  switch (severity) {
    case AlertSeverity.LOW:
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case AlertSeverity.MEDIUM:
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case AlertSeverity.HIGH:
      return 'text-orange-600 bg-orange-50 border-orange-200';
    case AlertSeverity.CRITICAL:
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getAlertStatusColor = (status: AlertStatus): string => {
  switch (status) {
    case AlertStatus.ACTIVE:
      return 'text-red-600 bg-red-50 border-red-200';
    case AlertStatus.ACKNOWLEDGED:
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case AlertStatus.RESOLVED:
      return 'text-green-600 bg-green-50 border-green-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getAlertTypeIcon = (type: AlertType): string => {
  switch (type) {
    case AlertType.DATA_GAP:
      return '📊'; // Chart icon
    case AlertType.GATEWAY_OFFLINE:
      return '📡'; // Satellite icon
    default:
      return '⚠️'; // Warning icon
  }
};

export const formatAlertTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return date.toLocaleDateString();
  }
};